import { Proposal, ProposalFund } from "creso-core/types";
import {
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "web-design-system";
import { Fragment, useMemo } from "react";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { formatAsCrore, formatAsPercentage } from "creso-core/utils";
import Amount from "@funds/components/atoms/amount";
import { GoalIcon } from "web-core";
import { ReportSection } from "./report-section";
import { StepUpLabel } from "./step-up-label";

interface RecommendationsSectionProps {
  proposal: Proposal;
  showSip: boolean;
  showLumpsum: boolean;
}

function SchemeTable({
  title,
  funds,
  proposal,
  total,
  showStepUp,
}: {
  title: string;
  funds: ProposalFund[];
  proposal: Proposal;
  total: string;
  showStepUp?: boolean;
}) {
  const schemeColumns: Array<{
    name: string;
    render: (data: ProposalFund, index: number) => React.ReactNode;
    className?: string;
  }> = useMemo(
    () => [
      {
        name: "",
        render: (_, index) => (
          <span className="text-sm text-muted-foreground">{index + 1}</span>
        ),
        className: "w-5",
      },
      {
        name: "Scheme name",
        render: (data) => (
          <div className="flex flex-row gap-3 items-center">
            <AmcLogo amc_code={data.amc_code} />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">
                {data.fund_detail?.fund_standard_name}
              </span>
              <span className="text-xs text-muted-foreground">
                {data.fund_detail?.broad_category_group}&nbsp;-&nbsp;
                {data.fund_detail?.fund_level_category_name}
              </span>
            </div>
          </div>
        ),
        className: "w-80",
      },
      {
        name: "Proposed amount",
        render: (data) => (
          <span className="text-sm font-semibold">
            <Amount precision={0}>
              {data.type === "LUMPSUM" ? data.lumpsum_amount : data.sip_amount}
            </Amount>
          </span>
        ),
        className: "text-left",
      },
      {
        name: "AUM",
        render: (data) => (
          <span className="text-sm">
            {formatAsCrore(data.fund_detail?.assets_under_management, 0)}
          </span>
        ),
        className: "text-left",
      },
      {
        name: "1 Year",
        render: (data) => (
          <span className="text-sm">
            {formatAsPercentage(data.fund_detail?.performance.return_1_year)}
          </span>
        ),
        className: "text-left",
      },
      {
        name: "3 Year",
        render: (data) => (
          <span className="text-sm">
            {formatAsPercentage(data.fund_detail?.performance.return_3_year)}
          </span>
        ),
        className: "text-left",
      },
      {
        name: "5 Year",
        render: (data) => (
          <span className="text-sm">
            {formatAsPercentage(data.fund_detail?.performance.return_5_year)}
          </span>
        ),
        className: "text-left",
      },
    ],
    []
  );

  return (
    <div className="flex flex-col">
      <div className="mb-5 flex items-center justify-between">
        <span className="text-lg font-semibold">{title}</span>
        <div className="flex items-center gap-3 rounded-lg bg-card px-3 py-2">
          <span className="text-sm font-semibold">Total:</span>
          <span className="text-sm font-semibold">
            <Amount precision={0}>{total}</Amount>
          </span>
          {showStepUp && <StepUpLabel proposal={proposal} />}
        </div>
      </div>
      <Table className="border border-border rounded-lg">
        <TableHeader className="bg-muted rounded-t-lg">
          <TableRow>
            {schemeColumns.map((column) => (
              <TableHead className={column.className} key={column.name}>
                {column.name}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        {funds.map((fund, idx) => {
          const { remarks } = fund;
          const showRemarks = proposal.include_remarks && !!remarks;
          return (
            <TableBody key={fund.fund_scheme} className="break-inside-avoid">
              <TableRow className={cn(showRemarks && "pb-0", "border-0")}>
                {schemeColumns.map((column) => (
                  <TableCell
                    className={cn(
                      column.className,
                      "py-2",
                      showRemarks && "pb-0"
                    )}
                    key={column.name}
                  >
                    {column.render(fund, idx)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow
                className={cn("py-2 pt-0 border-0", !showRemarks && "hidden")}
              >
                <TableCell className="py-2 pt-0" colSpan={3} />
                <TableCell className="py-2 pt-0" colSpan={4}>
                  <div className="flex flex-col gap-1 p-3 bg-card rounded-md">
                    <span className="text-xs font-semibold">Remarks</span>
                    <ul className="list-disc pl-4 text-xs text-muted-foreground">
                      {remarks
                        .split("\n")
                        .map((line) => line.replace(/^\s*[-•*]\s*/, "").trim())
                        .filter(Boolean)
                        .map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                    </ul>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          );
        })}
      </Table>
    </div>
  );
}

export function RecommendationsSection({
  proposal,
  showSip,
  showLumpsum,
}: RecommendationsSectionProps) {
  const sipRecommendations = proposal.proposal_funds.filter(
    (f) => f.type === "SIP"
  );
  const lumpsumRecommendations = proposal.proposal_funds.filter(
    (f) => f.type === "LUMPSUM"
  );

  const isGoalLinked = Boolean(proposal.goal);
  const showSummaryHeader = !isGoalLinked && !proposal?.projected_value;

  const header = (
    <ConditionalRenderer condition={showSummaryHeader}>
      <div className="flex flex-row mt-4 mb-6 items-center gap-3">
        <GoalIcon category="CUSTOM" />
        <span className="text-2xl font-semibold">
          {proposal.name || "Proposal for Wealth Creation"}
        </span>
      </div>
      <div className="flex">
        <div className="border border-border bg-card rounded-xl flex flex-row gap-12 p-5">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">One-time</span>
            <span className="text-base font-semibold">
              <Amount precision={0}>{proposal.total_lumpsum_amount}</Amount>
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Monthly SIP</span>
            <span className="text-base font-semibold">
              <Amount precision={0}>{proposal.total_sip_amount}</Amount>
            </span>
            <StepUpLabel proposal={proposal} />
          </div>
        </div>
      </div>
      <div className="w-full h-px bg-border my-8" />
    </ConditionalRenderer>
  );

  return (
    <Fragment>
      <ConditionalRenderer condition={showSip && sipRecommendations.length > 0}>
        <ReportSection sectionId="sip-recommendations" className="mb-8">
          {header}
          <SchemeTable
            title="SIP Recommendations"
            funds={sipRecommendations}
            proposal={proposal}
            total={proposal.total_sip_amount}
            showStepUp
          />
        </ReportSection>
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={showLumpsum && lumpsumRecommendations.length > 0}
      >
        <ReportSection sectionId="lumpsum-recommendations">
          {showSip && sipRecommendations.length > 0 ? null : header}
          <SchemeTable
            title="One time Recommendations"
            funds={lumpsumRecommendations}
            proposal={proposal}
            total={proposal.total_lumpsum_amount}
          />
        </ReportSection>
      </ConditionalRenderer>
    </Fragment>
  );
}
